<template>
  <div class="admin-car-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('adminCar.page.title')" :subtitle="$t('adminCar.page.subtitle')" />

    <el-row :gutter="16">
      <!-- 左侧：小区/车位/车牌类型树筛选 -->
      <el-col :xs="24" :sm="24" :md="6" :lg="5">
        <div class="ds-panel ds-role-tree">
          <community-parking-tree ref="communityParkingTree" @selectCommunity="handleSelectCommunity"
            @selectParkingArea="handleSelectParkingArea" @selectLeaseType="handleSelectLeaseType" />
        </div>
      </el-col>

      <el-col :xs="24" :sm="24" :md="18" :lg="19">
        <!-- (2) SearchPanel -->
        <ds-search-panel>
          <el-row :gutter="16">
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="searchForm.carNumLike" clearable class="w-full"
                :placeholder="$t('adminCar.search.carNum')" @keyup.enter.native="handleSearch" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="searchForm.num" clearable class="w-full"
                :placeholder="$t('adminCar.search.parkingNum')" @keyup.enter.native="handleSearch" />
            </el-col>
          </el-row>

          <template #actions>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">
              {{ $t('common.search') }}
            </el-button>
            <el-button icon="el-icon-refresh-left" @click="handleReset">
              {{ $t('common.reset') }}
            </el-button>
          </template>
        </ds-search-panel>

        <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
        <div class="ds-panel">
          <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false"
            :show-refresh="true" @update:density="density = $event" @refresh="getList">
            <template #left>
              <span class="ds-panel__title">{{ $t('adminCar.list.title') }}</span>
            </template>
          </ds-toolbar>

          <ds-data-table :data="tableData" :loading="loading" :error="hasError" :error-message="errorMessage"
            :density="density" border :empty-description="$t('adminCar.page.empty')" @retry="getList">
            <el-table-column prop="carNum" :label="$t('adminCar.table.carNum')" align="center" min-width="120">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.carNum || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="memberCarCount" :label="$t('adminCar.table.memberCar')" align="center" width="100" />
            <el-table-column prop="roomName" :label="$t('adminCar.table.roomName')" align="center" min-width="150"
              show-overflow-tooltip />
            <el-table-column :label="$t('adminCar.table.leaseType')" align="center" width="110">
              <template slot-scope="scope">
                <ds-status-tag size="sm" :type="scope.row.leaseType === 'T' ? 'warning' : 'primary'"
                  :label="scope.row.leaseType === 'T' ? $t('adminCar.leaseType.temp') : (scope.row.leaseTypeName || '-')" />
              </template>
            </el-table-column>
            <el-table-column prop="carTypeName" :label="$t('adminCar.table.carType')" align="center" width="110" />
            <el-table-column prop="carColor" :label="$t('adminCar.table.color')" align="center" width="90" />
            <el-table-column :label="$t('adminCar.table.owner')" align="center" min-width="150">
              <template slot-scope="scope">
                <el-link type="primary" :href="`/#/views/aCommunity/adminOwnerDetail?ownerId=${scope.row.ownerId}`"
                  target="_blank">
                  {{ scope.row.ownerName || '-' }}({{ scope.row.link }})
                </el-link>
              </template>
            </el-table-column>
            <el-table-column :label="$t('adminCar.table.parking')" align="center" width="140">
              <template slot-scope="scope">
                {{ scope.row.areaNum && scope.row.state === '1001' ? `${scope.row.areaNum}-${scope.row.num}` : $t('adminCar.parking.released') }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('adminCar.table.validDate')" align="center" width="170">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.startTime || '-' }}</span><br>
                <span class="ds-num">{{ scope.row.endTime || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('adminCar.table.status')" align="center" width="120">
              <template slot-scope="scope">
                <ds-status-tag size="sm" :type="resolveCarState(scope.row).type" :label="resolveCarState(scope.row).label" />
                <div v-if="scope.row.iotStateName" class="ds-cell-sub">{{ scope.row.iotStateName }}</div>
              </template>
            </el-table-column>
            <el-table-column prop="remark" :label="$t('adminCar.table.remark')" align="center" min-width="120"
              show-overflow-tooltip />
            <el-table-column :label="$t('common.operation')" align="center" width="100" fixed="right">
              <template slot-scope="scope">
                <el-button size="mini" @click="handleDetail(scope.row)">{{ $t('common.detail') }}</el-button>
              </template>
            </el-table-column>
          </ds-data-table>

          <ds-pagination-bar :page="pagination.current" :size="pagination.size" :total="pagination.total"
            @current-change="handleCurrentChange" @size-change="handleSizeChange" />
        </div>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { queryAdminOwnerCars } from '@/api/car/adminCarApi'
import CommunityParkingTree from '@/components/car/CommunityParkingTree'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'

export default {
  name: 'AdminCarList',
  components: {
    CommunityParkingTree,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar,
    DsStatusTag
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      searchForm: {
        communityId: '',
        paId: '',
        leaseType: '',
        carNumLike: '',
        num: '',
        carTypeCd: '1001'
      },
      tableData: [],
      pagination: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.getList()
    setTimeout(() => {
      if (this.$refs.communityParkingTree) {
        this.$refs.communityParkingTree.open()
      }
    }, 1000)
  },
  methods: {
    async getList() {
      try {
        this.loading = true
        this.hasError = false
        const params = {
          ...this.searchForm,
          page: this.pagination.current,
          row: this.pagination.size
        }
        const { data, total, records } = await queryAdminOwnerCars(params)
        this.tableData = data
        this.pagination.total = total
        this.pagination.records = records
      } catch (error) {
        this.hasError = true
        this.errorMessage = this.$t('adminCar.fetchError')
        this.$message.error(this.$t('adminCar.fetchError'))
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.pagination.current = 1
      this.getList()
    },
    handleReset() {
      this.searchForm.carNumLike = ''
      this.searchForm.num = ''
      this.pagination.current = 1
      this.getList()
    },
    handleSizeChange(val) {
      this.pagination.size = val
      this.getList()
    },
    handleCurrentChange(val) {
      this.pagination.current = val
      this.getList()
    },
    handleDetail(row) {
      this.$router.push({ path: `/pages/car/adminCarDetail?memberId=${row.carId}` })
    },
    handleSelectCommunity({ communityId }) {
      this.searchForm.communityId = communityId
      this.searchForm.paId = ''
      this.searchForm.leaseType = ''
      this.pagination.current = 1
      this.getList()
    },
    handleSelectParkingArea({ paId }) {
      this.searchForm.communityId = ''
      this.searchForm.paId = paId
      this.searchForm.leaseType = ''
      this.pagination.current = 1
      this.getList()
    },
    handleSelectLeaseType({ leaseType }) {
      this.searchForm.communityId = ''
      this.searchForm.paId = ''
      this.searchForm.leaseType = leaseType
      this.pagination.current = 1
      this.getList()
    },
    resolveCarState(car) {
      if (car.state === '3003') {
        return { type: 'danger', label: this.$t('adminCar.status.expired') }
      }
      const endTime = new Date(car.endTime)
      return endTime.getTime() > Date.now()
        ? { type: 'success', label: this.$t('adminCar.status.normal') }
        : { type: 'danger', label: this.$t('adminCar.status.expired') }
    }
  }
}
</script>

<style lang="scss" scoped>
.admin-car-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}
</style>
